import type { BaseTierId, AddOnId, Gender } from "./types"
import type { PriceMap } from "@/lib/pricing"
import { addOnPriceKey } from "./product-mapping"
import { TIER_TO_SERVICE } from "./coverage"

/**
 * Builds the request body for the insurance-checker's `/api/proforma-request`
 * endpoint (https://insurance.aheadhealth.com), which the quiz calls directly
 * — the endpoint is CORS-open for cross-origin embeds and runs in **auto mode**
 * by default: it creates the proforma record, generates the PDF, and returns it
 * inline for instant download (mirrors `CoverageForm.tsx` in that repo).
 */

/** insurance-checker host. Public, CORS-open; override for preview testing. */
export const INSURANCE_CHECKER_URL =
  process.env.NEXT_PUBLIC_INSURANCE_CHECKER_URL ??
  "https://insurance.aheadhealth.com"

/**
 * Quiz add-on id → insurance-checker proforma add-on. `key` is the Supabase
 * humanReadableId the proforma generator resolves to a priced line item
 * (insurance-checker lib/addon-labels.ts). Hormone is gender-specific.
 * `trend_overlay` is a free analysis layer with no proforma line item.
 */
function addonForProforma(
  id: AddOnId,
  gender: Gender | undefined
): { key: string; name: string } | null {
  switch (id) {
    case "hormone_test":
      return gender === "female"
        ? {
            key: "hormone-blood-test-women",
            name: "Hormone blood test (women)",
          }
        : { key: "hormone-blood-test-men", name: "Hormone blood test (men)" }
    case "omega_test":
      return { key: "omega-3-6-blood-test", name: "Omega-3 & 6 blood test" }
    case "b_vitamins":
      return {
        key: "essential-b-vitamins-blood-test",
        name: "Essential B-vitamins blood test",
      }
    case "calcium_score":
      return {
        key: "heart-ct-calcium-scoring-with-low-dose-radiation",
        name: "Heart CT calcium score",
      }
    case "dexa_scan":
      return { key: "dexa-scan-low-dose-radiation", name: "DEXA scan" }
    case "mamma_mri":
      return { key: "mamma-mri-with-contrast-dye", name: "Mamma MRI" }
    case "trend_overlay":
      return null
  }
}

export interface ProformaAddon {
  key: string
  name: string
  price: number
}

export interface ProformaRequestBody {
  email: string
  firstname: string
  lastname: string
  gender?: Gender
  insurer_id?: string
  package_id?: string
  service_id: string
  addons?: ProformaAddon[]
  locale: string
}

export function buildProformaRequestBody(args: {
  email: string
  name: string
  locale: string
  baseTier: BaseTierId
  addOns: AddOnId[]
  gender?: Gender
  insurerId?: string
  insurerPackageId?: string
  prices: PriceMap
}): ProformaRequestBody {
  const {
    email,
    name,
    locale,
    baseTier,
    addOns,
    gender,
    insurerId,
    insurerPackageId,
    prices,
  } = args

  const trimmed = name.trim()
  const spaceIdx = trimmed.indexOf(" ")
  const firstname = spaceIdx === -1 ? trimmed : trimmed.slice(0, spaceIdx)
  const lastname = spaceIdx === -1 ? "" : trimmed.slice(spaceIdx + 1).trim()

  const addons = addOns
    .map((id): ProformaAddon | null => {
      const mapped = addonForProforma(id, gender)
      if (!mapped) return null
      const priceKey = addOnPriceKey(id, gender)
      const price = priceKey ? Number(prices[priceKey] ?? 0) : 0
      return { key: mapped.key, name: mapped.name, price }
    })
    .filter((a): a is ProformaAddon => a !== null)

  return {
    email,
    firstname,
    lastname,
    gender,
    insurer_id: insurerId && insurerId !== "none" ? insurerId : undefined,
    package_id: insurerPackageId,
    service_id: TIER_TO_SERVICE[baseTier],
    addons: addons.length > 0 ? addons : undefined,
    locale,
  }
}
